import { call, put, takeLatest } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import api from "service/apiService";
import { ApiResponse } from "types";
import { translate } from "utils";
import { CommissionPolicy } from "../types";
import {
	getCommissionPoliciesAsync,
	getCommissionPolicyDetailsAsync,
	GetCommissionPoliciesAsyncPayload,
	GetCommissionPolicyDetailsPayload,
	setCommissionPolicies,
	CreateCommissionPolicyPayload,
	createNewCommissionPolicyAsync,
	setCommissionPolicy,
	updateCommissionPolicyAsync,
	UpdateCommissionPolicyPayload,
} from "./commission-policy.slice";

// Get list
function* getCommissionPoliciesSaga(action: PayloadAction<GetCommissionPoliciesAsyncPayload>) {
	const { query, callback } = action.payload;
	const skip = query.skip * (query.take || 0);
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<CommissionPolicy[]>>(
				"/dashboard/affiliates/commissions",
				{
					...query,
					skip,
					...(!!query.filter ? {filter: JSON.stringify(query.filter)} : {})
				},
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		yield put({ type: setCommissionPolicies.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetCommissionPolicies() {
	yield takeLatest(getCommissionPoliciesAsync, getCommissionPoliciesSaga);
}

// Get commissionPolicy details
function* getCommissionPolicyDetailsSaga(
	action: PayloadAction<GetCommissionPolicyDetailsPayload>,
) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<CommissionPolicy>>(
				`/dashboard/affiliates/commissions/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setCommissionPolicy.type, payload: response });
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchCommissionPolicyDetails() {
	yield takeLatest(getCommissionPolicyDetailsAsync, getCommissionPolicyDetailsSaga);
}

// Create new commissionPolicy
function* createNewCommissionPolicySaga(action: PayloadAction<CreateCommissionPolicyPayload>) {
	const { data, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post<ApiResponse<CommissionPolicy>>(
				"/dashboard/affiliates/commissions",
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate("Create successful"));
		callback(response.data as CommissionPolicy);
	} else {
		message.error(translate(error.message));
	}
}

function* watchCreateCommissionPolicy() {
	yield takeLatest(createNewCommissionPolicyAsync, createNewCommissionPolicySaga);
}

// Update commission policy
function* updateCommissionPolicySaga(action: PayloadAction<UpdateCommissionPolicyPayload>) {
	const { data, id, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.patch(
				`/dashboard/affiliates/commissions/${id}`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		yield put({ type: getCommissionPolicyDetailsAsync.type, payload: {id } });
		message.success(translate("Update successful"));
	} else {
		message.error(translate(error.message));
	}
}

function* watchUpdateCommissionPolicy() {
	yield takeLatest(updateCommissionPolicyAsync, updateCommissionPolicySaga);
}

const commissionPolicySaga = [
	watchGetCommissionPolicies(),
	watchCommissionPolicyDetails(),
	watchCreateCommissionPolicy(),
	watchUpdateCommissionPolicy(),
];
export default commissionPolicySaga;
